import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { addDays, addMonths, format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface CreateSubscriptionTransactionOnTypeChangeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  tenantName: string;
  subscriptionType: string; // cortesia, testers, promotion
  onSuccess: () => void;
}

export function CreateSubscriptionTransactionOnTypeChangeModal({
  open,
  onOpenChange,
  tenantId,
  tenantName,
  subscriptionType,
  onSuccess,
}: CreateSubscriptionTransactionOnTypeChangeModalProps) {
  const [periodType, setPeriodType] = useState<"lifetime" | "limited">("lifetime");
  const [periodDurationType, setPeriodDurationType] = useState<"days" | "months">("months");
  const [periodValue, setPeriodValue] = useState<string>("1");
  const [observations, setObservations] = useState("");

  const createTransactionMutation = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Usuário não autenticado");

      // Calcular datas
      const startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      
      let expirationDate: Date | null = null;
      
      if (periodType === "lifetime") {
        expirationDate = null; // NULL = lifetime
      } else {
        // Tempo limitado
        const period = parseInt(periodValue, 10);
        if (periodDurationType === "days") {
          if (isNaN(period) || (period !== 1 && period !== 7)) {
            throw new Error("Para dias, informe apenas 1 ou 7 dias");
          }
          expirationDate = addDays(startDate, period);
        } else {
          if (isNaN(period) || period < 1 || period > 24) {
            throw new Error("Número de meses inválido (1-24)");
          }
          expirationDate = addMonths(startDate, period);
        }
        expirationDate.setHours(23, 59, 59, 999);
      }

      // Criar transação
      const transaction = {
        tenant_id: tenantId,
        transaction_type: "type_change",
        payment_method: subscriptionType,
        payment_type: subscriptionType,
        subscription_plan_id: "plus",
        amount: 0,
        status: "approved",
        payment_date: startDate.toISOString(),
        expiration_date: expirationDate ? expirationDate.toISOString() : null,
        created_by: user.id,
        metadata: {
          payment_type: subscriptionType,
          period_type: periodType,
          period_duration_type: periodType === "limited" ? periodDurationType : null,
          period_value: periodType === "limited" ? parseInt(periodValue, 10) : null,
          lifetime: periodType === "lifetime",
          observations,
          created_by_name: user.email,
          note: `Transação criada ao mudar tipo para ${subscriptionType} (${periodType === "lifetime" ? "para sempre" : `${periodValue} ${periodDurationType === "days" ? "dias" : "meses"}`})`,
        },
      };

      // Inserir transação
      const { error: transactionError } = await supabase
        .from("subscription_transactions")
        .insert(transaction);

      if (transactionError) throw transactionError;

      // Atualizar tenant
      console.log("CreateTransactionOnTypeChange: subscriptionType recebido:", subscriptionType);
      const updateData: any = {
        subscription_status: "active",
        subscription_plan_id: "plus",
        subscription_type: subscriptionType,
        subscription_payment_method: subscriptionType,
        subscription_last_payment_date: startDate.toISOString(),
      };
      
      console.log("CreateTransactionOnTypeChange: UpdateData:", updateData);

      if (expirationDate) {
        updateData.subscription_next_billing_date = expirationDate.toISOString();
      } else {
        updateData.subscription_next_billing_date = null;
      }

      const { error: tenantError } = await supabase
        .from("tenants")
        .update(updateData)
        .eq("id", tenantId);

      if (tenantError) {
        console.error("CreateTransactionOnTypeChange: Erro ao atualizar tenant:", tenantError);
        throw tenantError;
      }

      // Verificar o que foi realmente atualizado no banco
      const { data: updatedTenant, error: fetchError } = await supabase
        .from("tenants")
        .select("subscription_type, subscription_payment_method")
        .eq("id", tenantId)
        .single();

      if (fetchError) {
        console.error("CreateTransactionOnTypeChange: Erro ao buscar tenant atualizado:", fetchError);
      } else if (updatedTenant) {
        console.log("CreateTransactionOnTypeChange: Tenant atualizado no banco:", updatedTenant);
        
        // Validar se o tipo foi atualizado corretamente
        if (updatedTenant.subscription_type !== subscriptionType) {
          console.error("CreateTransactionOnTypeChange: ERRO - subscription_type não corresponde!", {
            esperado: subscriptionType,
            atual: updatedTenant.subscription_type
          });
          throw new Error(`Erro: subscription_type não foi atualizado corretamente. Esperado: ${subscriptionType}, Atual: ${updatedTenant.subscription_type}`);
        }
      }
    },
    onSuccess: () => {
      const typeLabel = subscriptionType === 'cortesia' ? 'Cortesia' : subscriptionType === 'testers' ? 'Beta Tester' : 'Promoção';
      const periodLabel = periodType === "lifetime" 
        ? "para sempre" 
        : `${periodValue} ${periodDurationType === "days" ? (parseInt(periodValue, 10) === 1 ? "dia" : "dias") : (parseInt(periodValue, 10) === 1 ? "mês" : "meses")}`;
      
      toast.success(`Transação ${typeLabel} (${periodLabel}) criada com sucesso!`);
      setPeriodType("lifetime");
      setPeriodDurationType("months");
      setPeriodValue("1");
      setObservations("");
      onSuccess();
    },
    onError: (error: any) => {
      toast.error("Erro ao criar transação: " + error.message);
    },
  });

  const handleCreate = () => {
    createTransactionMutation.mutate();
  };

  const startDate = new Date();
  startDate.setHours(0, 0, 0, 0);
  
  let finalDate: Date | null = null;
  if (periodType === "limited") {
    const period = parseInt(periodValue, 10) || 1;
    if (periodDurationType === "days") {
      finalDate = addDays(startDate, period);
    } else {
      finalDate = addMonths(startDate, period);
    }
  }

  const getSubscriptionTypeLabel = () => {
    const labels: Record<string, string> = {
      cortesia: "Cortesia",
      testers: "Beta Tester",
      promotion: "Promoção",
    };
    return labels[subscriptionType] || subscriptionType;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Criar Transação de Assinatura</DialogTitle>
          <DialogDescription>
            Criar transação para <strong>{tenantName}</strong> ao mudar para tipo <strong>{getSubscriptionTypeLabel()}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Tipo de Período</Label>
            <RadioGroup value={periodType} onValueChange={(value) => {
              setPeriodType(value as "lifetime" | "limited");
              if (value === "limited") {
                setPeriodValue("1");
              }
            }}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="lifetime" id="lifetime" />
                <Label htmlFor="lifetime" className="font-normal cursor-pointer">
                  Para sempre (sem vencimento)
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="limited" id="limited" />
                <Label htmlFor="limited" className="font-normal cursor-pointer">
                  Tempo limitado
                </Label>
              </div>
            </RadioGroup>
          </div>

          {periodType === "limited" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="duration-type">Unidade de Tempo</Label>
                <Select value={periodDurationType} onValueChange={(value) => {
                  setPeriodDurationType(value as "days" | "months");
                  setPeriodValue("1");
                }}>
                  <SelectTrigger id="duration-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="days">Dias</SelectItem>
                    <SelectItem value="months">Meses</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="period-value">
                  {periodDurationType === "days" ? "Número de Dias (1 ou 7)" : "Número de Meses (1-24)"}
                </Label>
                {periodDurationType === "days" ? (
                  <Select value={periodValue} onValueChange={setPeriodValue}>
                    <SelectTrigger id="period-value">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 dia</SelectItem>
                      <SelectItem value="7">7 dias</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    id="period-value"
                    type="number"
                    min="1"
                    max="24"
                    value={periodValue}
                    onChange={(e) => setPeriodValue(e.target.value)}
                  />
                )}
                {finalDate && (
                  <p className="text-xs text-muted-foreground">
                    Expira em: {format(finalDate, "dd/MM/yyyy", { locale: ptBR })}
                  </p>
                )}
              </div>
            </>
          )}

          {periodType === "lifetime" && (
            <div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-lg">
              <p className="text-sm text-blue-900 dark:text-blue-100">
                ⚠️ Esta assinatura será <strong>para sempre</strong> (sem vencimento). O tenant terá acesso permanente.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="observations">Observações (opcional)</Label>
            <Textarea
              id="observations"
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              placeholder="Adicione observações sobre esta transação..."
              rows={3}
            />
          </div>

          <div className="bg-muted p-4 rounded-lg space-y-2">
            <p className="text-sm font-medium">Esta ação irá:</p>
            <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
              <li>Criar 1 transação na tabela subscription_transactions</li>
              <li>Tipo: {getSubscriptionTypeLabel()}</li>
              <li>Ativar o tenant como "active"</li>
              {periodType === "lifetime" ? (
                <li>Duração: <strong>Para sempre</strong> (sem vencimento)</li>
              ) : finalDate ? (
                <li>Expira em: {format(finalDate, "dd/MM/yyyy", { locale: ptBR })}</li>
              ) : null}
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createTransactionMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCreate}
            disabled={createTransactionMutation.isPending || (periodType === "limited" && (!periodValue || parseInt(periodValue, 10) < 1))}
          >
            {createTransactionMutation.isPending 
              ? "Criando..." 
              : periodType === "lifetime"
              ? "Criar Para Sempre"
              : `Criar por ${periodValue} ${periodDurationType === "days" ? (parseInt(periodValue, 10) === 1 ? "Dia" : "Dias") : (parseInt(periodValue, 10) === 1 ? "Mês" : "Meses")}`
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

